import type { ForecastModel, Scenario, ScenarioType } from '@/types';
import { api } from './client';

export interface CreateScenarioDto {
  name: string;
  description?: string;
  planVersionId: string;
  scenarioType?: ScenarioType;
  color?: string;
  sortOrder?: number;
}

export interface UpdateScenarioDto {
  name?: string;
  description?: string;
  scenarioType?: ScenarioType;
  color?: string;
  sortOrder?: number;
}

/** Current forecasts of 2–4 scenarios from one plan (GET /scenarios/compare). */
export interface ScenarioComparison {
  planVersion: { id: string; name: string; version: number; periodType: string };
  /** Reporting currency of every amount; null when no scenario has a forecast. */
  currency: string | null;
  /** Scenario that variance is measured against: the baseline if selected, else the first. */
  referenceScenarioId: string;
  /** Period starts (YYYY-MM-DD) covered by any compared forecast. */
  periods: string[];
  scenarios: {
    id: string;
    name: string;
    scenarioType: ScenarioType;
    isBaseline: boolean;
    color: string | null;
    assumptionCount: number;
    /** Null when the scenario has no completed forecast run. */
    forecast: { runId: string; model: ForecastModel; completedAt: string | null } | null;
    total: number | null;
    variance: number | null;
    variancePercent: number | null;
    /** Aligned with periods; null where the forecast has no rows for that period. */
    periodAmounts: (number | null)[];
    /** Forecast rows left out of the totals because no exchange rate was available. */
    excludedRows: number;
  }[];
}

export const scenarioService = {
  async getAll(params?: { planVersionId?: string }): Promise<Scenario[]> {
    try {
      const queryParams = params?.planVersionId ? { planVersionId: params.planVersionId } : undefined;
      const result = await api.get<Scenario[]>('/scenarios', queryParams);

      // Ensure we always return an array
      if (!result) {
        return [];
      }

      if (!Array.isArray(result)) {
        return [result] as Scenario[];
      }

      return result;
    } catch {
      return [];
    }
  },

  async getById(id: string): Promise<Scenario> {
    if (!id) {
      throw new Error('Scenario ID is required');
    }
    return api.get<Scenario>(`/scenarios/${id}`);
  },

  async create(dto: CreateScenarioDto): Promise<Scenario> {
    if (!dto.name?.trim()) {
      throw new Error('Scenario name is required');
    }
    if (!dto.planVersionId) {
      throw new Error('Plan version ID is required');
    }
    return api.post<Scenario>('/scenarios', dto);
  },

  async update(id: string, dto: UpdateScenarioDto): Promise<Scenario> {
    if (!id) {
      throw new Error('Scenario ID is required');
    }
    return api.patch<Scenario>(`/scenarios/${id}`, dto);
  },

  async delete(id: string): Promise<void> {
    if (!id) {
      throw new Error('Scenario ID is required');
    }
    await api.delete(`/scenarios/${id}`);
  },

  async compare(scenarioIds: string[]): Promise<ScenarioComparison> {
    if (!scenarioIds || scenarioIds.length < 2) {
      throw new Error('Select at least two scenarios to compare');
    }
    return api.get<ScenarioComparison>('/scenarios/compare', { ids: scenarioIds.join(',') });
  },

  async clone(id: string, name: string): Promise<Scenario> {
    if (!id) {
      throw new Error('Scenario ID is required');
    }
    if (!name?.trim()) {
      throw new Error('New scenario name is required');
    }
    return api.post<Scenario>(`/scenarios/${id}/clone`, { name });
  },

  async setBaseline(id: string): Promise<Scenario> {
    if (!id) {
      throw new Error('Scenario ID is required');
    }
    return api.post<Scenario>(`/scenarios/${id}/set-baseline`);
  },
};
